import { ApiError } from 'api-clients/monolith';
import { Home } from 'lucide-react';
import { usePostHog } from '@/hooks/use-posthog';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import NotFoundIcon from '@/assets/icons/not-found.svg';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useSourceFreemium } from '@/hooks/use-source-freemium';

import { usePostingBatch, useRetryPostingItem } from '../use-posting-batch';

import { OpeningResultCard } from './opening-result-card';
import { ResultHeader } from './result-header';
import { ResultStats } from './result-stats';

import type { OpeningResult, PostingResultData } from '../types';

const CARD_CLASS =
  'border-fx-neutral-200 flex w-full flex-col items-center gap-6 overflow-y-auto rounded-lg border bg-white px-12 py-8 shadow-sm';

/** The result step of `/post-job`, read from the flow's in-memory result. */
export function PostingResult() {
  const { postingResult } = useJobPostingContext();

  if (!postingResult) return null;

  return <PostingResultView result={postingResult} />;
}

interface PostingResultViewProps {
  result: PostingResultData;
  startedBy?: string | null;
  /** Present while a row has not settled once; replaces the stats. */
  progress?: { settled: number; total: number };
  /** Read once per card mount. Legacy result cards start closed. */
  isCardExpanded?: (opening: OpeningResult) => boolean;
  retry?: ReturnType<typeof useRetryPostingItem>;
}

function PostingResultView({
  result,
  startedBy,
  progress,
  isCardExpanded,
  retry,
}: PostingResultViewProps) {
  const { scenario, summary, openings } = result;

  return (
    <div className={CARD_CLASS}>
      <ResultHeader
        scenario={scenario}
        isRunning={!!progress}
        startedBy={startedBy}
      />

      <Separator />

      {progress ? (
        <PostingProgress {...progress} />
      ) : (
        <ResultStats summary={summary} scenario={scenario} />
      )}

      <div className="flex w-full flex-col gap-2">
        <Typography
          variant="caption"
          className="text-fx-neutral-500 tracking-wider uppercase"
        >
          <FormattedMessage
            defaultMessage="Openings posted"
            description="Section title for the list of opening results"
          />
        </Typography>

        <ul className="flex flex-col gap-2">
          {openings.map(opening => (
            <OpeningResultCard
              key={opening.openingId}
              opening={opening}
              defaultExpanded={isCardExpanded?.(opening)}
              retry={retry}
            />
          ))}
        </ul>
      </div>

      <Separator />

      <Typography variant="small1" className="text-fx-neutral-900 text-center">
        {scenario === 'all-failed' ? (
          <FormattedMessage
            defaultMessage="You were not charged for any postings."
            description="Footer message when all postings failed"
          />
        ) : (
          <FormattedMessage
            defaultMessage="We'll monitor performance and highlight when your posting needs attention."
            description="Footer message after successful or partial job posting"
          />
        )}
      </Typography>

      <LeaveButton />
    </div>
  );
}

function LeaveButton() {
  const { dismissResult } = useJobPostingContext();
  const isFreemium = useSourceFreemium();

  return (
    <Button
      type="button"
      className="bg-gradient-to-b from-[#3935ff] to-[#8f00ff] text-white hover:opacity-90"
      onClick={dismissResult}
    >
      <Home className="size-4" />
      {isFreemium ? (
        <FormattedMessage
          defaultMessage="Campaigns"
          description="Button to go to the campaigns list after posting, for accounts without a Source subscription"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Dashboard"
          description="Button to navigate to dashboard after posting"
        />
      )}
    </Button>
  );
}

function PostingProgress({
  settled,
  total,
}: {
  settled: number;
  total: number;
}) {
  const intl = useIntl();
  const percent = total > 0 ? Math.round((settled / total) * 100) : 0;
  const label = intl.formatMessage(
    {
      defaultMessage: '{settled} of {total} postings done',
      description:
        'Posting page progress: postings that settled out of all postings in the bulk post',
    },
    { settled, total },
  );

  return (
    <div className="flex w-full flex-col gap-2">
      <Typography variant="body2">{label}</Typography>
      <Progress value={percent} aria-label={label} aria-valuenow={percent} />
    </div>
  );
}

const isUnsettled = (opening: OpeningResult) =>
  opening.channels.some(
    c =>
      c.status === 'failed' || c.status === 'queued' || c.status === 'posting',
  );

/**
 * `/:accountSlug/post-job/:bulkPostId`: one posting batch, live. It never
 * reads `hire-source-async-job-posting`, so a batch stays readable after the
 * flag turns off.
 */
export function PostingPage() {
  const { bulkPostId = '' } = useParams<{ bulkPostId: string }>();
  const { data: batch, result, error } = usePostingBatch(bulkPostId);
  const retry = useRetryPostingItem(bulkPostId);
  const posthog = usePostHog();

  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (hasTrackedPageViewRef.current) return;
    hasTrackedPageViewRef.current = true;
    posthog.capture('source_job_post:posting_page_view', {
      posting_batch_id: bulkPostId,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error && !batch) {
    if (error instanceof ApiError && error.status === 404) {
      return <PostingNotFound />;
    }
    throw error;
  }
  if (!batch || !result) return <PostingPageSkeleton />;

  // `settled` counts rows that settled at least once and survives a Retry,
  // so the header stays on the summary while a retried row runs again.
  const isRunning = batch.settled < batch.total;

  return (
    <PostingResultView
      result={result}
      startedBy={batch.started_by.name}
      progress={
        isRunning ? { settled: batch.settled, total: batch.total } : undefined
      }
      isCardExpanded={isRunning ? () => true : isUnsettled}
      retry={retry}
    />
  );
}

function PostingPageSkeleton() {
  return (
    <div className={CARD_CLASS} aria-busy="true">
      <div className="flex w-full flex-col gap-3">
        <Skeleton className="h-7 w-80" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-5 w-full" />
      </div>
      <Separator />
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-2 w-full" />
      </div>
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        {['a', 'b', 'c', 'd', 'e', 'f'].map(key => (
          <Skeleton key={key} className="h-12 w-full" />
        ))}
      </div>
    </div>
  );
}

PostingPage.Skeleton = PostingPageSkeleton;

function PostingNotFound() {
  const intl = useIntl();
  const { dismissResult } = useJobPostingContext();
  const isFreemium = useSourceFreemium();

  return (
    <div className="flex w-full items-center justify-center py-16">
      <EmptyState
        icon={NotFoundIcon}
        header={intl.formatMessage({
          defaultMessage: "We couldn't find this posting.",
          description:
            'Posting page title for an unknown address or a posting the user may not see',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Open it from the banner on your dashboard or from the status email.',
          description: 'Posting page hint below the not-found title',
        })}
        descriptionVariant="body1"
        descriptionClassName="text-fx-neutral-600"
        buttonTitle={
          isFreemium
            ? intl.formatMessage({
                defaultMessage: 'Campaigns',
                description:
                  'Button to go to the campaigns list after posting, for accounts without a Source subscription',
              })
            : intl.formatMessage({
                defaultMessage: 'Dashboard',
                description: 'Button to navigate to dashboard after posting',
              })
        }
        onClick={dismissResult}
      />
    </div>
  );
}
